/**
 * Cross-module interface contracts.
 *
 * This is the ONLY sanctioned coupling between top-level modules. A module may
 * import from `@/contracts` and from its own directory. It must not reach into
 * another module's internals. If you need something new from a sibling module,
 * add it here first.
 */
import type * as THREE from 'three';

// ---------------------------------------------------------------------------
// Time & simulation
// ---------------------------------------------------------------------------

/** Fixed simulation step in seconds. The sim is deterministic at this rate. */
export const FIXED_DT = 1 / 60;

export interface FrameTime {
  /** Seconds since boot, wall clock. */
  elapsed: number;
  /** Render delta in seconds, already clamped against spiral-of-death. */
  delta: number;
  /** Fixed step count consumed this frame. */
  steps: number;
  /** 0..1 blend between previous and current sim state, for render interpolation. */
  alpha: number;
}

/**
 * One read of the permanent frame-time instrument.
 *
 * Three clocks, because on a vsync-locked page only one of them is honest about
 * headroom: `p*` is the wall interval between frames (what the player feels, but
 * clamped to the refresh rate), `cpu*` is time spent inside the engine tick, and
 * `gpu*` is `EXT_disjoint_timer_query_webgl2` elapsed time around the render —
 * the number the spec's frame-time budget is actually about. Percentiles, not a
 * mean: a 9 ms average with 40 ms hitches and a steady 14 ms feel nothing alike.
 */
export interface PerfSnapshot {
  /** Frames in the current window. Percentiles are meaningless below ~30. */
  samples: number;
  p50: number;
  p95: number;
  p99: number;
  mean: number;
  min: number;
  max: number;
  /** Frames per second implied by the median wall interval. */
  fps: number;
  cpuP50: number;
  cpuP99: number;
  /** Zero when the timer-query extension is unavailable — check `gpuSupported`. */
  gpuP50: number;
  gpuP99: number;
  gpuSupported: boolean;
  /** Last frame's renderer counters. */
  calls: number;
  triangles: number;
  /** Worst single frame since the last reset. */
  peakCalls: number;
  peakTriangles: number;
  programs: number;
  geometries: number;
  textures: number;
  /** Long main-thread tasks (>50 ms) observed since page start. Present only
   *  when the dev longtask logger is active (`import.meta.env.DEV` or `?perf=1`). */
  longTasks?: number;
  /** How many of those landed during boot (behind the loading screen). */
  longTasksBoot?: number;
  /** Worst single long task in ms since page start. */
  longTaskWorstMs?: number;
}

/**
 * An all-zero snapshot, for headless probes and tests that stand up a fake
 * `EngineContext` with no render loop behind it. Never returned by the engine.
 */
export function zeroPerfSnapshot(): PerfSnapshot {
  return {
    samples: 0, p50: 0, p95: 0, p99: 0, mean: 0, min: 0, max: 0, fps: 0,
    cpuP50: 0, cpuP99: 0, gpuP50: 0, gpuP99: 0, gpuSupported: false,
    calls: 0, triangles: 0, peakCalls: 0, peakTriangles: 0,
    programs: 0, geometries: 0, textures: 0,
  };
}

// ---------------------------------------------------------------------------
// Art direction — the "Paper Sunshine" colour script
// ---------------------------------------------------------------------------

/**
 * The one colour script (docs/art-bible.md §2). Every baker family — materials,
 * world, vehicles, terrain — imports its colour from here; no local hex literal
 * survives in any of them. All hex values are authored in sRGB, exactly what
 * `bake.ts srgb()` expects. `BLACK_FLOOR` alone is a linear-space triple, since
 * it is applied inside the tonemap where the frame is still linear.
 */
export const ART = {
  // -- register anchors (§2.1) ------------------------------------------------
  /** UI panels, cloud lit face; the warm white anchor. */
  PAPER: 0xfff8ec,
  /** UI text only — never a surface black. */
  INK: 0x3e3a36,
  /** Post split-tone shadows + cool component of the AO tint. */
  SHADOW_TONE: 0x5e6fa8,
  /** Warm AO multiply target (post composite, §4.6). */
  SHADOW_WARM: 0x6b4a33,
  /** Post split-tone highlights. */
  HIGHLIGHT_TONE: 0xfff4dc,
  /** Tonemap floor, linear RGB — nothing in frame reads ink-black. */
  BLACK_FLOOR: [0.030, 0.028, 0.040] as const,
  /** Warm translucent grime wash replacing soil/soot dirt (§5). */
  GRIME_WASH: 0x7a6248,

  // -- terrain (§2.2) ---------------------------------------------------------
  terrain: {
    DIRT: 0xc68a4f,
    PACKED_EARTH: 0xb07a45,
    MUD: 0x8a6242,
    SAND: 0xf2d48a,
    CLAY: 0xd9995c,
    GRAVEL: 0xb8a88f,
    GRASS: 0x7fbf56,
    DRY_GRASS: 0xb9c866,
    ROCK: 0x9c917f,
    ASPHALT: 0x6e6b66,
    CONCRETE: 0xc9c2b4,
    ROAD_MARK: 0xfff3d9,
    ROAD_MARK_YELLOW: 0xf3b928,
  },

  // -- machines: two-hue colour identity (§2.3) --------------------------------
  /**
   * Every machine class is a colour *pair* — primary body + secondary trim
   * (counterweights, fenders, boom stripes, bucket/bed interior). Single-hue
   * liveries are banned. Crane covers both tower and mobile cranes.
   */
  machines: {
    // Sandbox liveries (docs/sand-design.md §3.1): proportion/colour language of
    // the real machines, no logos, no trademarks. The excavator secondary is a
    // warm espresso, not a grey — HSL chroma 0.169 passes the §2.6 ban while
    // reading as "black" beside amber.
    excavator: { primary: 0xffb300, secondary: 0x4f3b24 }, // "the yellow-and-black one"
    wheelLoader: { primary: 0x4ca83c, secondary: 0xffde4d }, // "the green-and-yellow one"
    dumpTruck: { primary: 0xffde4d, secondary: 0x4ca83c }, // "the yellow-and-green one"
    crane: { primary: 0xffc63d, secondary: 0x2e4a62 }, // "the gold-and-blue one"
  },
  /** Shared machine kit colours (§2.3). */
  machineKit: {
    ACCENT_STEEL_BLUE: 0x2e4a62,
    HYDRAULIC_RAM: 0xd8dce0,
    /** Tyres: warm charcoal body with painted tread. */
    TYRE: 0x3b3b3f,
    TYRE_TREAD: 0x55524e,
    /** Cab glass is opaque, with a baked white diagonal sheen stripe. */
    CAB_GLASS: 0xbfe8f7,
    BEACON: 0xffd54f,
  },

  // -- vegetation & structures (§2.4) ------------------------------------------
  vegetation: {
    CANOPY_TOP: 0x6fbf4f,
    CANOPY_SHADE: 0x4e9a3c,
    AUTUMN: 0xe8a33c,
    TRUNK: 0x8a6248,
    GROUND_COVER: 0x8fc96a,
  },
  structures: {
    TIMBER: 0xe3b77c,
    TIMBER_CUT: 0xf2d9a8,
    BRICK: 0xc96f4e,
    STEEL_FRAME: 0x7fa3c4,
    SAFETY: 0xff8a3c,
    SAFETY_STRIPE: 0xfff8ec,
  },

  // -- sand: the Paper Sunshine sand family (docs/sand-design.md §7.5) ---------
  sand: {
    /** Dry warm-cream sand (matches terrain.SAND). */
    DRY: 0xf2d48a,
    /** Wet-sand darkening band near water. */
    WET: 0xbe9455,
    /** Slope/shade tint on cut faces. */
    SHADE: 0xd9b368,
    /** Molded castle-piece surface. */
    CASTLE: 0xefcb82,
  },

  // -- sky, perceived post-grade (§2.5) ----------------------------------------
  sky: {
    NOON_ZENITH: 0x4fa8e8,
    NOON_HORIZON: 0xcdebf2,
    GOLDEN_ZENITH: 0xffb36b,
    GOLDEN_HORIZON: 0xffe3b8,
    /** Storybook night — never black. */
    NIGHT_ZENITH: 0x1e2b58,
    NIGHT_HORIZON: 0x35477d,
    MOONLIGHT: 0xafc4ff,
  },
} as const;

/** Anything that advances on the fixed simulation clock. */
export interface FixedUpdatable {
  fixedUpdate(dt: number): void;
}

/** Anything that runs once per rendered frame (animation, interpolation, FX). */
export interface FrameUpdatable {
  update(t: FrameTime): void;
}

export interface Disposable {
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Preload & jobs (docs/preload-design.md §2.1)
// ---------------------------------------------------------------------------

/** A unit of warm-up or mid-session work. `step` runs until done; it must return
 *  within ~4 ms per call by doing a bounded slice of work and returning false to
 *  continue. */
export interface SlicedJob {
  /** Stable id for logging/dedup, e.g. "materials.rebake:concrete". */
  readonly id: string;
  /** Do one slice. `budgetMs` is advisory; return true when finished. */
  step(budgetMs: number): boolean;
  /** Optional: called if the job is cancelled (tier changed again mid-queue). */
  cancel?(): void;
}

export type PreloadPhaseId =
  | 'textures'   // CPU bakes: material surfaces, vehicle liveries, facades
  | 'geometry'   // merged machine geometry, blueprint primitives
  | 'materials'  // material object creation (cheap, but ordered before shaders)
  | 'physics'    // Rapier pre-touch: one body/collider/joint of each used type
  | 'audio'      // engine-cycle wavetables for every machine kind
  | 'shaders';   // compileAsync warm-up — always last, needs everything above

/** How a material will be used at runtime, so the shader warm-up rig compiles
 *  the exact program permutation the scene will ask for. */
export interface MaterialWarmupOptions {
  /** Also warm the material's shadow-depth variant. */
  shadow?: boolean;
  /** Material is used on an InstancedMesh. */
  instanced?: boolean;
  vertexColors?: boolean;
  /** Custom geometry attributes the runtime mesh carries (e.g. 'aAtlas'). A
   *  program only matches if the warm geometry has the same attribute set. */
  attributes?: readonly string[];
}

/** One registration for the shader warm-up rig. Exactly one of `material` /
 *  `object` is non-null. */
export interface WarmupEntry {
  readonly material: THREE.Material | null;
  readonly object: THREE.Object3D | null;
  readonly opts: MaterialWarmupOptions | null;
}

export interface PreloadRegistry {
  /** Register a sliced job in a named phase. Called from GameModule.preload(). */
  add(phase: PreloadPhaseId, label: string, job: SlicedJob): void;
  /** Convenience for one-shot work small enough to run inside a single slice. */
  addStep(phase: PreloadPhaseId, label: string, run: () => void): void;
  /** Register a material for the shader warm-up rig. Idempotent per material
   *  instance. `opts.shadow` also warms its depth variant. */
  warmMaterial(m: THREE.Material, opts?: MaterialWarmupOptions): void;
  /** Register a whole object tree (uses its real geometries/attributes). */
  warmObject(o: THREE.Object3D): void;
}

export interface JobScheduler {
  /** Enqueue mid-session sliced work. 'interactive' preempts 'background'. */
  enqueue(job: SlicedJob, priority?: 'interactive' | 'background'): void;
  /** Cancel every queued job with this id (calls their `cancel?()`). */
  cancel(id: string): void;
  readonly pending: number;
}

/** Inert scheduler for tests and headless probes that stand up a fake
 *  `EngineContext`. Never used by the engine. */
export function noopJobScheduler(): JobScheduler {
  return { enqueue() { /* noop */ }, cancel() { /* noop */ }, pending: 0 };
}

// ---------------------------------------------------------------------------
// Module lifecycle
// ---------------------------------------------------------------------------

/**
 * Every top-level module exposes one of these. `init` may be async (WASM, texture
 * bake, worker spawn); everything after boot must be synchronous and allocation-free
 * on the hot path.
 */
export interface GameModule extends Partial<FixedUpdatable>, Partial<FrameUpdatable>, Partial<Disposable> {
  readonly name: string;
  /** Lower numbers initialise and update first. */
  readonly order: number;
  init(ctx: EngineContext): Promise<void> | void;
  /** Register warm-up work. Runs after every module's init(), behind the
   *  loading screen. Must not touch the renderer directly — register jobs. */
  preload?(ctx: EngineContext, reg: PreloadRegistry): void;
}

/** Handed to every module at init. The single shared spine of the game. */
export interface EngineContext {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly canvas: HTMLCanvasElement;
  readonly events: EventBus;
  readonly input: InputState;
  readonly settings: Settings;
  readonly assets: AssetRegistry;
  /** Time-sliced (~4 ms/frame) scheduler for mid-session work: tier re-bakes,
   *  FX rebuilds, deferred builds. Nothing may block longer than one frame. */
  readonly jobs: JobScheduler;
  /**
   * Rolling frame-time percentiles plus this frame's renderer counters. Always
   * live — the perf overlay and the screenshot harness read the same object, so
   * a review round can never again land without a measured frame time.
   */
  perfSnapshot(): PerfSnapshot;
  /** Resolve a module by name once boot completes. Throws if absent. */
  get<T extends GameModule>(name: string): T;
  /** Resolve a module, or undefined if it is not registered. */
  tryGet<T extends GameModule>(name: string): T | undefined;
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/**
 * Typed pub/sub. Add new events to `GameEvents` rather than inventing string
 * channels, so the compiler catches renames.
 */
export interface GameEvents {
  'sim:paused': { paused: boolean };
  'quality:changed': { tier: QualityTier };
  'terrain:deformed': { center: Vec3; radius: number; volumeDelta: number };
  'payload:changed': { machineId: string; material: MaterialKind; litres: number };
  'machine:entered': { machineId: string };
  'machine:exited': { machineId: string };
  'structure:stage': { structureId: string; stage: BuildStage; complete: boolean };
  'contract:offered': { contractId: string };
  'contract:accepted': { contractId: string };
  'contract:completed': { contractId: string; reward: number; stars: number };
  'economy:changed': { balance: number; delta: number; reason: string };
  'unlock:granted': { kind: 'machine' | 'area' | 'blueprint'; id: string };
  'tutorial:step': { stepId: string };
  'sand:scooped': { center: Vec3; litres: number; machineId: string | null };
  'sand:poured': { center: Vec3; litres: number; machineId: string | null };
  'sand:moldFilled': { moldId: string; fill01: number };
  'sand:moldReady': { moldId: string; piece: SandPieceKind };
  'sand:piecePlaced': { pieceId: string; piece: SandPieceKind; position: Vec3; snapped: boolean };
  'sand:pieceCrumbled': { pieceId: string; piece: SandPieceKind; position: Vec3; litres: number };
  'sand:treasureFound': { treasureId: string; position: Vec3 };
  'audio:oneshot': { sound: string; position?: Vec3; volume?: number; pitch?: number };
  'fx:impact': { position: Vec3; normal: Vec3; material: MaterialKind; energy: number };
  'notify': { text: string; icon?: string; tone?: 'info' | 'good' | 'warn' };
}

export interface EventBus {
  on<K extends keyof GameEvents>(k: K, fn: (p: GameEvents[K]) => void): () => void;
  once<K extends keyof GameEvents>(k: K, fn: (p: GameEvents[K]) => void): () => void;
  off<K extends keyof GameEvents>(k: K, fn: (p: GameEvents[K]) => void): void;
  emit<K extends keyof GameEvents>(k: K, p: GameEvents[K]): void;
}

// ---------------------------------------------------------------------------
// Math value types (plain data — safe to serialise, cheap to copy)
// ---------------------------------------------------------------------------

export interface Vec3 { x: number; y: number; z: number }
export interface Quat { x: number; y: number; z: number; w: number }
export interface Transform { position: Vec3; rotation: Quat }

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

/**
 * Semantic actions, not keys. Modules read intent; the input module owns the
 * mapping from keyboard / gamepad / touch to these.
 */
export type AxisAction =
  | 'move.forward'   // -1..1 throttle / reverse
  | 'move.steer'     // -1..1 left / right
  | 'look.x' | 'look.y'
  | 'tool.boom'      // -1..1 raise / lower
  | 'tool.stick'     // -1..1 extend / retract
  | 'tool.bucket'    // -1..1 curl / dump
  | 'tool.slew'      // -1..1 rotate cab
  | 'tool.aux';      // -1..1 secondary (outriggers, blade tilt, drum...)

export type ButtonAction =
  | 'action.primary' | 'action.secondary'
  | 'brake' | 'handbrake' | 'horn' | 'lights' | 'beacon'
  | 'enterExit' | 'cameraCycle' | 'resetMachine'
  | 'ui.menu' | 'ui.jobs' | 'ui.map' | 'ui.photo' | 'ui.back';

export interface InputState {
  /** Current value of an analogue action, already deadzoned and curved. */
  axis(a: AxisAction): number;
  /** True while held. */
  down(b: ButtonAction): boolean;
  /** True only on the frame the button went down. */
  pressed(b: ButtonAction): boolean;
  /** True only on the frame the button came up. */
  released(b: ButtonAction): boolean;
  /** Pointer delta in CSS pixels since last frame. */
  readonly pointerDelta: { x: number; y: number };
  readonly pointerLocked: boolean;
  readonly lastDevice: 'keyboard' | 'gamepad' | 'touch';
}

// ---------------------------------------------------------------------------
// Settings & quality
// ---------------------------------------------------------------------------

export type QualityTier = 'low' | 'medium' | 'high' | 'ultra';

export interface Settings {
  quality: QualityTier;
  shadows: boolean;
  ssao: boolean;
  bloom: boolean;
  antialias: 'off' | 'smaa' | 'taa';
  renderScale: number;      // 0.5 .. 1.0
  drawDistance: number;     // metres
  vegetationDensity: number;// 0..1
  masterVolume: number;
  sfxVolume: number;
  musicVolume: number;
  assists: boolean;         // kid-friendly assists master switch
  invertLookY: boolean;
  colorblindSafe: boolean;
  uiScale: number;          // 0.8 .. 1.6
  reducedMotion: boolean;
  get<K extends keyof Settings>(k: K): Settings[K];
  set<K extends keyof Settings>(k: K, v: Settings[K]): void;
  save(): void;
}

// ---------------------------------------------------------------------------
// Materials (the substance kind, not the THREE.Material kind)
// ---------------------------------------------------------------------------

export type MaterialKind =
  | 'dirt' | 'clay' | 'sand' | 'gravel' | 'rubble'
  | 'concrete' | 'asphalt' | 'water' | 'snow'
  | 'timber' | 'steel' | 'brick' | 'grass' | 'rock';

/** Bulk physical properties used by digging, tipping and FX. */
export interface MaterialProfile {
  kind: MaterialKind;
  /** kg per m³ loose. */
  density: number;
  /** Angle of repose in radians — governs how piles settle. */
  repose: number;
  /** Resistance to being cut by a bucket edge, 0..1. */
  hardness: number;
  /** Loose volume / in-situ volume. Dug dirt occupies more space. */
  swellFactor: number;
  /** Base tint used by FX (dust, spray) when no texture is available. */
  dustColor: number;
}

/** Generated PBR texture set. All maps share UV space and tiling. */
export interface PbrTextureSet {
  id: string;
  map: THREE.Texture;
  normalMap: THREE.Texture;
  roughnessMap: THREE.Texture;
  aoMap?: THREE.Texture;
  displacementMap?: THREE.Texture;
  /** World-space metres covered by one UV tile. */
  tileMetres: number;
}

export interface MaterialLibrary extends GameModule {
  readonly name: 'materials';
  /** Bake-on-first-use, cached thereafter. */
  texture(id: string, quality?: QualityTier): PbrTextureSet;
  /** Ready-to-use THREE material, shared across all consumers. */
  surface(id: string): THREE.Material;
  profile(kind: MaterialKind): MaterialProfile;
}

// ---------------------------------------------------------------------------
// Terrain
// ---------------------------------------------------------------------------

export interface TerrainSystem extends GameModule {
  readonly name: 'terrain';
  /** World height in metres at (x, z). Bilinear-sampled. */
  heightAt(x: number, z: number): number;
  /** Surface normal at (x, z), unit length. */
  normalAt(x: number, z: number, out?: Vec3): Vec3;
  /** Dominant surface material at (x, z), for FX and traction. */
  materialAt(x: number, z: number): MaterialKind;
  /**
   * Raise or lower terrain inside a soft-edged disc.
   * @param delta metres at the centre; negative digs, positive fills.
   * @returns cubic metres of material actually moved (clamped by bedrock/ceiling).
   */
  deform(x: number, z: number, radius: number, delta: number): number;
  /** Cheap query used by the dig solver before committing a deform. */
  volumeInDisc(x: number, z: number, radius: number, targetY: number): number;
  /** True once the initial heightfield and colliders exist. */
  readonly ready: boolean;
  readonly sizeMetres: number;

  // --- collider grid -------------------------------------------------------
  // `refreshTerrainChunk(cx, cz)` addresses a chunk of *this* grid, so physics
  // has to be able to describe it in world space. Without these the chunk
  // coordinates are meaningless to anyone but terrain itself.

  /** Metres between heightfield posts. */
  readonly postMetres: number;
  /** World-space footprint of one chunk, in metres. */
  readonly chunkMetres: number;
  /** Chunks along each axis; chunk coordinates run `0 .. chunksPerAxis - 1`. */
  readonly chunksPerAxis: number;
  /** Posts along one chunk edge, inclusive of both borders. */
  readonly chunkPosts: number;
}

// ---------------------------------------------------------------------------
// Physics
// ---------------------------------------------------------------------------

export type BodyHandle = number;

export interface RayHit {
  body: BodyHandle | null;
  point: Vec3;
  normal: Vec3;
  distance: number;
}

/** Standalone fixed heightfield collider description (sand zones). */
export interface HeightfieldDesc {
  /** World-space min corner. */
  origin: Vec3;
  /** Posts per side (square field). */
  posts: number;
  cellMetres: number;
  /** posts² world-Y heights, row-major (`iz * posts + ix`). Retained by reference. */
  heights: Float32Array;
}

export interface PhysicsSystem extends GameModule {
  readonly name: 'physics';
  raycast(origin: Vec3, dir: Vec3, maxDistance: number, exclude?: BodyHandle): RayHit | null;
  /** Overlap test used for pickup/attach zones. */
  sphereOverlap(centre: Vec3, radius: number): BodyHandle[];
  transformOf(h: BodyHandle, out?: Transform): Transform | null;
  velocityOf(h: BodyHandle, out?: Vec3): Vec3 | null;
  applyImpulse(h: BodyHandle, impulse: Vec3, atPoint?: Vec3): void;
  setKinematicTarget(h: BodyHandle, t: Transform): void;
  /** Rebuild the collider for one terrain chunk after deformation. */
  refreshTerrainChunk(cx: number, cz: number): void;
  /** Register a standalone fixed heightfield collider (sand zones). */
  createHeightfield(desc: HeightfieldDesc): BodyHandle;
  /** Re-read the (mutated, same-instance) heights array. No allocation. */
  updateHeightfield(h: BodyHandle, heights: Float32Array): void;
  /** Fixed cuboid collider for placed castle pieces. */
  createFixedCuboid(t: Transform, halfExtents: Vec3): BodyHandle;
  removeBody(h: BodyHandle): void;
}

// ---------------------------------------------------------------------------
// Vehicles
// ---------------------------------------------------------------------------

export type MachineKind =
  | 'excavator' | 'wheelLoader' | 'dumpTruck' | 'bulldozer'
  | 'towerCrane' | 'mobileCrane' | 'concreteMixer' | 'roadRoller'
  | 'backhoe' | 'telehandler' | 'paver' | 'forklift';

export interface PayloadState {
  material: MaterialKind | null;
  /** Current load in litres (1 m³ = 1000 L). */
  litres: number;
  capacityLitres: number;
}

export interface MachineState {
  readonly id: string;
  readonly kind: MachineKind;
  readonly displayName: string;
  readonly transform: Transform;
  readonly speedKph: number;
  readonly payload: PayloadState;
  /** 0..1 per named hydraulic actuator, for HUD and audio load. */
  readonly actuators: Readonly<Record<string, number>>;
  /** World position of the working edge (bucket teeth, fork tips, hook). */
  toolTip(out?: Vec3): Vec3;
  readonly occupied: boolean;
}

export interface VehicleSystem extends GameModule {
  readonly name: 'vehicles';
  spawn(kind: MachineKind, at: Vec3, headingRad?: number): MachineState;
  despawn(id: string): void;
  get(id: string): MachineState | undefined;
  list(): readonly MachineState[];
  /** The machine the player currently controls, if any. */
  readonly active: MachineState | null;
  enter(id: string): boolean;
  exit(): void;
  /** Nearest enterable machine within range of a world point. */
  nearest(to: Vec3, maxDistance: number): MachineState | null;
}

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

export type BuildStage =
  | 'survey' | 'excavate' | 'foundation' | 'frame'
  | 'walls' | 'roof' | 'services' | 'finish';

export interface StageRequirement {
  stage: BuildStage;
  /** Material demand in litres, keyed by kind. */
  demand: Partial<Record<MaterialKind, number>>;
  /** Machines that can satisfy this stage. Empty means any. */
  machines: MachineKind[];
  /** 0..1 completion of this stage. */
  progress: number;
}

export interface StructureState {
  readonly id: string;
  readonly blueprintId: string;
  readonly displayName: string;
  readonly origin: Vec3;
  readonly stages: readonly StageRequirement[];
  readonly currentStage: BuildStage;
  readonly complete: boolean;
}

export interface BuildSystem extends GameModule {
  readonly name: 'build';
  blueprints(): readonly { id: string; name: string; icon: string; unlocked: boolean }[];
  place(blueprintId: string, at: Vec3, headingRad: number): StructureState | null;
  /** Feed material into the active stage. Returns litres actually consumed. */
  contribute(structureId: string, material: MaterialKind, litres: number): number;
  get(id: string): StructureState | undefined;
  list(): readonly StructureState[];
}

// ---------------------------------------------------------------------------
// Sand (src/sand/) — docs/sand-design.md §7
// ---------------------------------------------------------------------------

export type SandPieceKind =
  | 'brick' | 'wall' | 'tower' | 'turret' | 'arch' | 'battlement'
  | 'plank' | 'scaffold';

export interface SandZoneDesc {
  readonly id: number;
  /** World-space min corner of the zone grid. */
  readonly origin: Vec3;
  readonly sizeMetres: number;
  readonly cellMetres: number;
}

export interface SandPieceDef {
  readonly kind: SandPieceKind;
  /** Litres of carried sand a mold of this piece consumes; 0 for wood proxies. */
  readonly sandLitres: number;
  /** Bounding collider half extents, metres. */
  readonly halfExtents: Vec3;
  /** Placement grid pitch, metres. */
  readonly snapMetres: number;
  /** HUD pictogram id (no text, art-bible §2.6). */
  readonly icon: string;
}

export interface SandMoldState {
  readonly id: string;
  readonly kind: SandPieceKind;
  readonly position: Vec3;
  readonly fillLitres: number;
  readonly capacityLitres: number;
  /** A finished piece is waiting on the outfeed pad. */
  readonly ready: boolean;
}

export interface SandPieceState {
  readonly id: string;
  readonly kind: SandPieceKind;
  readonly transform: Transform;
}

export interface SandGhostState {
  readonly kind: SandPieceKind;
  readonly transform: Transform;
  /** True when magnet-snapped to the grid or a neighbouring piece edge. */
  readonly snapped: boolean;
}

export interface SandSystem extends GameModule {
  readonly name: 'sand';
  readonly ready: boolean;

  // -- field queries (any thread of play, allocation-free with `out` params) --
  zones(): readonly SandZoneDesc[];
  /** Zone id at (x, z), or null outside every zone. */
  zoneAt(x: number, z: number): number | null;
  /** Sand depth in metres above base terrain; 0 outside zones. */
  depthAt(x: number, z: number): number;
  /** Top-of-sand world height; equals terrain height outside zones. */
  surfaceHeightAt(x: number, z: number): number;

  // -- field edits (call from fixedUpdate only) --
  /** Remove up to maxLitres from a soft disc. Returns litres actually removed. */
  scoop(center: Vec3, radius: number, maxLitres: number): number;
  /** Deposit litres over a soft disc. Returns litres deposited. */
  pour(center: Vec3, radius: number, litres: number): number;
  /** Press a shallow persistent mark (tracks, footprints). depthMetres ≤ 0.05. */
  imprint(center: Vec3, radius: number, depthMetres: number): void;
  /** Dozer-blade flatten toward the local mean, volume conserved.
   *  strength 0..1. Returns litres displaced (drives FX/audio intensity). */
  level(center: Vec3, radius: number, strength: number): number;

  // -- molds & castle pieces --
  pieceCatalog(): readonly SandPieceDef[];
  molds(): readonly SandMoldState[];
  nearestMold(to: Vec3, maxDistance: number): SandMoldState | null;
  /** Pour carried sand into a mold. Returns litres consumed. */
  fillMold(moldId: string, litres: number): number;
  /** Magnet the ready piece off a mold's outfeed onto the active machine's tool. */
  carryFromMold(moldId: string): boolean;
  /** Pick a placed piece back up. Moving is free, never a failure. */
  carryPiece(pieceId: string): boolean;
  /** Piece currently magnet-carried by the active machine's tool, if any. */
  carried(): SandPieceKind | null;
  /** Live kite-ghost for the carried piece; null when nothing is carried. */
  ghost(): SandGhostState | null;
  /** Stamp the carried piece at the ghost. Emits 'sand:piecePlaced'. */
  commitPlacement(): SandPieceState | null;
  pieces(): readonly SandPieceState[];
  /** Pop a piece back into loose sand at its footprint. Emits 'sand:pieceCrumbled'. */
  crumble(pieceId: string): void;

  // -- bury & find --
  /** Hide a treasure under the sand. Returns its id. */
  buryTreasure(at: Vec3, icon?: string): string;
  /** 0..1 "getting warmer" toward the nearest unfound treasure. */
  treasureHint(to: Vec3): number;
}

// ---------------------------------------------------------------------------
// Gameplay / progression
// ---------------------------------------------------------------------------

export interface ContractObjective {
  id: string;
  text: string;
  progress: number; // 0..1
  done: boolean;
}

export interface Contract {
  id: string;
  title: string;
  brief: string;
  icon: string;
  difficulty: 1 | 2 | 3;
  reward: number;
  objectives: readonly ContractObjective[];
  accepted: boolean;
  completed: boolean;
}

export interface GameplaySystem extends GameModule {
  readonly name: 'gameplay';
  readonly balance: number;
  contracts(): readonly Contract[];
  accept(id: string): boolean;
  abandon(id: string): void;
  isUnlocked(kind: 'machine' | 'area' | 'blueprint', id: string): boolean;
}

// ---------------------------------------------------------------------------
// Audio & FX
// ---------------------------------------------------------------------------

export interface AudioSystem extends GameModule {
  readonly name: 'audio';
  /** Attach a looping, load-modulated engine voice to a machine. */
  attachEngine(machineId: string, kind: MachineKind): void;
  detachEngine(machineId: string): void;
  oneshot(sound: string, position?: Vec3, volume?: number, pitch?: number): void;
  setListener(position: Vec3, forward: Vec3, up: Vec3): void;
}

export interface FxSystem extends GameModule {
  readonly name: 'fx';
  dust(position: Vec3, amount: number, material: MaterialKind): void;
  exhaust(position: Vec3, direction: Vec3, load: number): void;
  spray(position: Vec3, direction: Vec3, material: MaterialKind, amount: number): void;
  /** Lay a persistent track/tyre decal on the terrain. */
  track(position: Vec3, headingRad: number, width: number, material: MaterialKind): void;
  sparks(position: Vec3, normal: Vec3, energy: number): void;
}

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

/** Everything is procedural, so this caches bakes rather than loading files. */
export interface AssetRegistry {
  /** Memoised factory. The key must be stable and fully describe the output. */
  cache<T>(key: string, make: () => T): T;
  has(key: string): boolean;
  /** Approximate GPU bytes held, for the perf HUD. */
  readonly estimatedBytes: number;
  dispose(key: string): void;
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

export interface RenderSystem extends GameModule {
  readonly name: 'render';
  /** Normalised time of day, 0 = midnight, 0.5 = noon. */
  timeOfDay: number;
  /** 0 = clear, 1 = overcast/storm. */
  weather: number;
  readonly sunDirection: Vec3;
  /**
   * Register a subtree for cascaded shadow casting. Every mesh under `o` casts
   * and receives, *unless* it carries its own answer in `userData.shadow`:
   *   `'off'`      — never casts, never receives. For anything whose shadow is
   *                  not worth a draw call in every cascade: chain-link, netting,
   *                  glazing, shrink-wrap, distant billboard cards.
   *   `'castOnly'` — casts but does not receive. For a merged shadow proxy
   *                  standing in for detailed meshes that receive on their own.
   */
  addShadowCaster(o: THREE.Object3D): void;
  /** Composite the frame. Called by the engine after all module updates. */
  render(t: FrameTime): void;
  /**
   * Force the full lighting/fog/grade state for a named scenario without
   * advancing the clock. Used by the shader warm-up pass and the screenshot
   * harness. Optional only until the render module lands its implementation
   * (docs/preload-design.md §2.1 — Agent B); core calls it defensively.
   */
  applyScenario?(s: RenderScenario): void;
  /**
   * Compile every registered warm-up entry under every lighting scenario the
   * session can produce, then run warm renders that touch the post chain and
   * shadow depth materials. Awaited by the `shaders` preload phase — the
   * loading screen does not lift until this resolves. Optional only until the
   * render module lands the rig (docs/preload-design.md §2.3 — Agent B).
   */
  warmup?(entries: readonly WarmupEntry[]): Promise<void>;
}

/** Named lighting/weather states the warm-up and screenshot harness can force. */
export type RenderScenario = 'noon' | 'dusk' | 'night' | 'overcast';
